import curry from 'lodash/curry';
import zip from 'lodash/zip';
import round from 'lodash/round';
import last from 'lodash/last';

import { colors } from 'ui/theme';
import { SeriesColorData, XAxisRange, YAxisRange } from '../types';

export function createXAxisData(): XAxisRange {
  return {
    xMin: 0,
    xMax: 100,
    xPoints: [
      0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
      26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48,
      49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71,
      72, 73, 74, 75, 76, 77, 78, 79, 80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94,
      95, 96, 97, 98, 99, 100,
    ],
  };
}

export function createYAxisData(data: Array<number> | undefined): YAxisRange {
  if (!data || !Array.isArray(data) || data.length < 1) {
    return {
      yMin: 0.0,
      yMax: 0.0,
      yPoints: [],
    };
  }

  return {
    yMin: 0.0,
    yMax: last(data) as number,
    yPoints: data,
  };
}

export function transformDataFromSlider(data: number) {
  return Math.round(data * 100);
}

export function transformDataToSlider(data: number) {
  return data / 100;
}

export function getSeriesGroups(
  xData: Array<number>,
  currentMin: number,
  currentMax: number,
): Array<SeriesColorData<Array<number>>> {
  if (xData.length <= 0) {
    return [];
  }

  const seriesStart = {
    data: xData.slice(0, currentMin + 1),
    fillColor: 'rgba(230, 251, 255, 0.8)',
    lineColor: colors.cyan100,
  };
  const seriesSelected = {
    data: xData.slice(currentMin, currentMax + 1),
    isSelectedSeries: true,
    fillColor: '#C2F0FC',
    lineColor: colors.cyan100,
  };
  const seriesEnd = {
    data: xData.slice(currentMax),
    fillColor: 'rgba(230, 251, 255, 0.8)',
    lineColor: colors.cyan100,
  };

  // We don't need overlap on end points
  if (seriesStart.data.length === 1) {
    seriesStart.data = [];
  }
  if (seriesEnd.data.length === 1) {
    seriesEnd.data = [];
  }

  return [seriesStart, seriesSelected, seriesEnd].filter((series) => series.data.length > 0);
}

export const createDataSeries = curry(function createDataSeries(
  zippingData: { lastY: null | number; yDataCopy: Array<number> },
  series: SeriesColorData<Array<number>>,
): SeriesColorData<[number | undefined, number | undefined][]> {
  let nextYSeries: Array<number> = [];
  if (zippingData.lastY !== null) {
    nextYSeries = [zippingData.lastY];
  }

  const { data: seriesData } = series;
  const nextYDataLength = zippingData.lastY !== null ? seriesData.length - 1 : seriesData.length;
  const yForZip = nextYSeries.concat(zippingData.yDataCopy.splice(0, nextYDataLength));

  zippingData.lastY = yForZip[yForZip.length - 1];
  return {
    ...series,
    data: zip<number, number>(seriesData, yForZip),
  };
});

export function createBandedDataPoints(
  xData: Array<number>,
  yData: Array<number>,
  currentMin: number,
  currentMax: number,
): Array<SeriesColorData<[number | undefined, number | undefined][]>> {
  const filteredSeries = getSeriesGroups(xData, currentMin, currentMax);

  // eslint-disable-next-line prefer-const
  let zippingData = {
    lastY: null,
    yDataCopy: yData.slice(),
  };
  const seriesMapper = createDataSeries(zippingData);
  const nextSeriesData = filteredSeries.map(seriesMapper);
  return nextSeriesData;
}

const abbreviationsByNumberLength = [
  [[4, 5, 6], 'K'],
  [[7, 8, 9], 'M'],
];
/**
 @description Return the appropriate english abbreviation for a give number based on digit length
 @param { number } numericLength: The length of the number. This will likely require a string or object conversion to obtain
 @returns { string } Contains the abbreviation letter to use based on the length
 */
export function findAbbreviationByNumberLength(numericLength: number): string {
  const maybeAbbreviation =
    abbreviationsByNumberLength.find((abbEntry) =>
      (abbEntry[0] as Array<number>).includes(numericLength),
    ) || [];
  if (maybeAbbreviation.length <= 0) {
    return '';
  }

  return maybeAbbreviation[1] as string;
}

/**
 @description Parses the leading significant numbers from the number given and returns as a string
 @param { number } data: The number to parse
 @returns { number } Contains the leading numbers

 1000 --> 1,
 23103 --> 23,
 978432 --> 978,
 5340000 --> 5.3,
 1532864246 --> 1.5,
 */
export function getLeadingNumbers(data: number): number {
  const dataString = String(data);
  if (dataString.length <= 3) {
    return Number(dataString);
  }

  const remainder = dataString.length % 3;
  let leadingValue;
  if (remainder === 0) {
    leadingValue = dataString.slice(0, 3);
  }
  if (remainder === 1) {
    leadingValue = dataString.slice(0, 2).split('').join('.');
  }
  if (remainder === 2) {
    leadingValue = dataString.slice(0, 2);
  }

  return Number(leadingValue);
}

export function getConversionFormat(data: number): { abbreviation: string; leadingNumber: number } {
  const numeralDataString = String(data).split('');
  const abbreviation = findAbbreviationByNumberLength(numeralDataString.length);
  const leadingNumber = getLeadingNumbers(data);
  return { abbreviation, leadingNumber };
}

export function getSignificantDigitRounded(data: number) {
  const rounded = round(data);
  return parseFloat(rounded.toPrecision(2));
}

export function roundConversions(data: number): string {
  const rounded = getSignificantDigitRounded(data);
  const { abbreviation, leadingNumber } = getConversionFormat(rounded);
  return `${leadingNumber}${abbreviation}`;
}

export function getMean(dataSet: Array<number>): number {
  if (dataSet.length <= 0) {
    return 0;
  }

  const sum = dataSet.reduce((totalSum, data) => totalSum + data, 0);
  return sum / dataSet.length;
}

export function getAxisCorrelation(startRange: number, endRange: number): Array<number> {
  const fromAxisMin = transformDataFromSlider(startRange);
  const fromAxisMax = transformDataFromSlider(endRange);
  return [fromAxisMin, fromAxisMax];
}

export function preserveTrailingFormatForZero(data: number): string | number {
  if (data === 0) {
    return '0.00';
  }
  return data;
}

const numberFormatter = new Intl.NumberFormat('en-US');
export function addCommas(data: number): string {
  return numberFormatter.format(data);
}

/**
 * @description Return the selected probability by the given selected range
 * @param { Array } probability - Array of the probabilities by percentile
 * @param { number } min - Current minimum value from selection, i.e. 0.50
 * @param { number } max - Current maximum value from selection, i.e. 0.50
 * @returns { Array<number> }
 */
export function getSelectedProbabilityRange(
  probability: Array<number> = [],
  min: number,
  max: number,
): Array<number> {
  const [convertedMin, convertedMax] = getAxisCorrelation(min, max);
  return probability.slice(convertedMin, convertedMax + 1);
}

export function getBoundedValues(
  probability: Array<number> = [],
  min: number,
  max: number,
): Array<number> {
  const { xMin, xMax } = createXAxisData();
  const [convertedMin, convertedMax] = getAxisCorrelation(min, max);
  const boundedMin = convertedMin === xMin ? min : probability[convertedMin];
  const boundedMax = convertedMax === xMax ? 1.0 : probability[convertedMax];
  return [boundedMin, boundedMax];
}

/**
 * @description Return the total number of contacts for a selected range
 * @param { number} totalUsers - Number of total users in the data model
 * @param { Array } probability - Array of the probabilities by percentile
 * @param { number } min - Current minimum value from selection, i.e. 0.50
 * @param { number } max - Current maximum value from selection, i.e. 0.50
 * @returns { number }
 */
export function getContactsSelected(totalUsers: number, min: number, max: number): string {
  return addCommas(Math.round((max - min) * totalUsers));
}

/**
 * @description Return the likely conversion ratio for a selected range
 * @param { Array } probability - Array of the probabilities by percentile
 * @param { number } min - Current minimum value from selection, i.e. 0.50
 * @param { number } max - Current maximum value from selection, i.e. 0.50
 * @returns { number | string }
 */
export function getConvertRatio(
  probability: Array<number>,
  min: number,
  max: number,
): number | string {
  const selectedProb = getSelectedProbabilityRange(probability, min, max);
  const rawRatio = getMean(selectedProb) / getMean(probability);
  return preserveTrailingFormatForZero(round(rawRatio, 2));
}

/**
 * @description Return the total, approximate(rounded) number of conversions predicted for a selected range
 * @param { number} totalUsers - Number of total users in the data model
 * @param { Array } probability - Array of the probabilities by percentile
 * @param { number } min - Current minimum value from selection, i.e. 0.50
 * @param { number } max - Current maximum value from selection, i.e. 0.50
 * @param { number | string } convertRatio - Calculated convert ratio used to round the prediction returned
 * @returns { string } A string containing formatted approximate total conversions predicted at a given range
 */
export function getConversionsPredicted(
  totalUsers: number,
  probability: Array<number>,
  min: number,
  max: number,
  convertRatio: number | string,
): string {
  const [convertedMin, convertedMax] = getAxisCorrelation(min, max);
  const usersInWindow = (max - min) * totalUsers;
  const selectedProbability = probability.slice(convertedMin, convertedMax + 1);
  const meanSelectedProbability = getMean(selectedProbability);
  if (Number(convertRatio) < 0.01) {
    return '0';
  }
  return roundConversions(meanSelectedProbability * usersInWindow);
}

export const donutColors: string[] = [
  colors.salmon50,
  colors.mustard50,
  colors.cyan50,
  colors.purple90,
  colors.green50,
  colors.tangerine100,
  colors.forest100,
  colors.cyan100,
  colors.purple50,
  colors.flamingo100,
  colors.cottoncandy100,
  colors.lime100,
];

/**
 * @description Return an array of objects to be passed to a DonutChart component as it's chartData
 * @param { any[] } dataToChart - Array of objects containing raw data to be passed as chartData to the chart component
 * @param { string } namePath - Path to "name" key in each individual object for output, this shows in the tooltip hover effect
 * @param { string } yPath - Path to "y" key in each individual object for output, this is turned by the chart into a relative percentage which determines relative donut chart slice size
 * @param { boolean } useOtherField - Determines whether data points that fall outside of the "threshold" chosen will be bucketed into their own 'Other' chart slice or left off the chart completely (defaults to no "Other" slice)
 * @param { number } thresholdValue - The value used to determine where the cutoff "threshold" lies, depending on the type of threshold chosen (in the case of "pieSlices", chart will show a maximum number of slices equal to the 'thresholdValue' chosen. In the case of 'percentageCutoff", chart will show as many slices that have 'y' values greater than or equal to the 'thresholdValue' chosen)
 * @param { "pieSlices" | "valueCutoff" } thresholdOptions - Sets the "threshold" used to populate the graph to be either the total number of data points to be shown on the graph ui or a "y" value weighting cutoff, number used set by "thresholdValue" (defaults to "pieSlices")
 * @param { string[] } pieColors - Sets the color series to be applied to fill the pie slices in the correct order
 * @returns { {name: string, y: number, color: string}[] } An array containing formatted data to be passed as a DonutChart component prop
 */
export function getDonutChartData(
  dataToChart: any[],
  namePath: string,
  yPath: string,
  useOtherField = false,
  thresholdValue = 12,
  thresholdOptions: 'pieSlices' | 'valueCutoff' = 'pieSlices',
  pieColors: string[] = donutColors,
): { name: string; y: number; color: string }[] {
  const orderedData = [...dataToChart].sort((a, b) => b[yPath] - a[yPath]);
  const formattedData: { name: string; y: number; color: string }[] = [];

  if (thresholdOptions === 'pieSlices') {
    orderedData.splice(0, thresholdValue).forEach((point, i) => {
      formattedData.push({
        name: point[namePath],
        y: point[yPath],
        color: pieColors[i % pieColors.length],
      });
    });

    if (useOtherField) {
      const otherObject = {
        name: 'Other',
        y: 0,
        color: pieColors[(thresholdValue - 1) % pieColors.length],
      };

      orderedData.slice(thresholdValue, orderedData.length).forEach((point) => {
        otherObject.y += point[yPath];
      });

      formattedData.push(otherObject);
    }
  } else if (thresholdOptions === 'valueCutoff') {
    orderedData
      .filter((f) => f[yPath] >= thresholdValue)
      .forEach((point, i) => {
        formattedData.push({
          name: point[namePath],
          y: point[yPath],
          color: pieColors[i % pieColors.length],
        });
      });

    if (useOtherField) {
      const otherObject = {
        name: 'Other',
        y: 0,
        color: pieColors[formattedData.length % pieColors.length],
      };

      orderedData.slice(formattedData.length, orderedData.length).forEach((point) => {
        otherObject.y += point[yPath];
      });

      formattedData.push(otherObject);
    }
  } else {
    throw new Error('');
  }

  return formattedData;
}
